import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { fetchProviders, fetchUsers } from '@/features/Addons/components/AddonFilters/utils';
import { getEnv, handleError } from '@/utils';

import AddonSettingsModal from '@/features/Addons/components/AddonSettingsModal';
import { AddonsTypes } from '@/features/Addons/types';
import { AirportGroupDropdown } from '@/components/AirportDropdown';
import { Badge } from 'react-bootstrap';
import Button from '@/common/Button';
import ConfirmationButton from '@/components/ConfirmationButton';
import { DATE_FE_FORMAT } from '@/constants';
import DateInput from '@/common/DateInput';
import FormButton from '@/components/FormButton';
import HasPermission from '@/components/HasPermission';
import QueryFilters from '@/components/QueryFilters';
import QuerySelect from '@/common/QuerySelect';
import SwitchInput from '@/common/SwitchInput';
import runArchiveAddonImports from '@/features/Addons/api/services/runArchiveImports';
import unarchiveAddonImports from '@/features/Addons/api/services/updateAddonImportBulk';
import { useAddonsState } from '@/features/Addons';

const AddonFilters = (): ReactElement => {
  const [
    { from, to, createdBy, airportCode, providerId, archived, fetch, selected, toggleModal, onReset, addonSettings, rememberFilters },
    setState,
  ] = useAddonsState(({ state, setState }) => [
    {
      from: state.filters.from,
      to: state.filters.to,
      createdBy: state.filters.createdBy,
      airportCode: state.filters.airportCode,
      providerId: state.filters.providerId,
      archived: state.filters.archived,
      fetch: state.fetch,
      selected: state.selected,
      toggleModal:
        (name: keyof AddonsTypes.Modals, show?: boolean): (() => void) =>
        (): void => {
          setState((current) => ({
            ...current,
            modals: {
              ...current.modals,
              [name]: { ...current.modals[name], show: show ?? !current.modals[name]?.show },
            },
          }));
        },
      onReset: state.onReset,
      addonSettings: state.modals.addonSettings,
      rememberFilters: state.settings.rememberFilters,
    },
    setState,
  ]);

  const handleChange =
    (key: keyof AddonsTypes.Filters) =>
    (value): void => {
      let newValue = value;
      setState((current) => {
        switch (key) {
          case 'airportCode':
            return { ...current, filters: { ...current.filters, [key]: typeof newValue === 'string' ? newValue.split(',') : newValue } };
          case 'from':
            return {
              ...current,
              filters: {
                ...current.filters,
                [key]: newValue,
                to: typeof newValue === 'string' && current.filters.to < newValue ? newValue : current.filters.to,
              },
            };
          case 'to':
            return {
              ...current,
              filters: {
                ...current.filters,
                [key]: newValue,
                from: typeof newValue === 'string' && current.filters.from > newValue ? newValue : current.filters.from,
              },
            };
          default:
            return { ...current, filters: { ...current.filters, [key]: newValue !== current.filters[key] ? newValue : '' } };
        }
      });
    };

  const archiveImports = useCallback(async (): Promise<void> => {
    try {
      await runArchiveAddonImports(selected);
      await fetch();
      setState((current) => ({ ...current, selected: [] }));
    } catch (err) {
      handleError(err);
    }
  }, [selected, fetch, setState]);

  const unarchiveImports = useCallback(async (): Promise<void> => {
    try {
      await unarchiveAddonImports(selected);
      await fetch();
      setState((current) => ({ ...current, selected: [] }));
    } catch (err) {
      handleError(err);
    }
  }, [selected, fetch, setState]);

  const getUsers = useCallback(async (): Promise<ReactElement[]> => fetchUsers(airportCode || []), [airportCode]);

  useEffect(() => {
    (rememberFilters ? fetch : onReset)();
    /* ONLY ON MOUNT
      Ignore other dependencies so this only fires on mount.
    */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const env = useMemo(getEnv, []);

  return (
    <>
      <QueryFilters className="justify-content-between">
        <div className="d-flex gap-2">
          <QueryFilters.Control>
            <DateInput name="from" value={from} onChange={(value: string): void => handleChange('from')(value)} format={DATE_FE_FORMAT} />
            <DateInput name="to" value={to} onChange={(value: string): void => handleChange('to')(value)} format={DATE_FE_FORMAT} />
          </QueryFilters.Control>
          <QueryFilters.Input>
            <QuerySelect
              name="createdBy"
              query={getUsers}
              value={createdBy}
              onChange={handleChange('createdBy')}
              placeholder="Imported By"
            />
          </QueryFilters.Input>
          <QueryFilters.Input>
            <AirportGroupDropdown
              value={airportCode}
              onChange={handleChange('airportCode')}
              options={{
                showClearButton: false,
                locale: {
                  'Select...': 'Airport',
                },
              }}
            />
            {/* <QuerySelect
              name="airportCode"
              query={fetchAirportGroups}
              value={airportCode}
              onChange={handleChange('airportCode')}
              placeholder="Airport"
            /> */}
          </QueryFilters.Input>
          <QueryFilters.Input>
            <QuerySelect
              name="providerId"
              onChange={handleChange('providerId')}
              value={providerId}
              query={fetchProviders}
              placeholder="Airline"
            />
          </QueryFilters.Input>
          <QueryFilters.Control>
            <Button name="SUBMIT_SEARCH" variant="success" className="border-white" onClick={fetch}>
              GO
            </Button>
            <Button name="RESET_SEARCH" variant="outline-light" onClick={onReset}>
              Reset
            </Button>
          </QueryFilters.Control>
        </div>
        <div className="d-flex justify-content-end gap-2">
          <HasPermission name={'allowViewArchivedImports'}>
            <QueryFilters.Control>
              <div className="h-100 d-flex flex-column justify-content-center me-2 text-secondary-subtle">
                <span>Archives:</span>
              </div>
              <div className="{line-height:3rem;}">
                <SwitchInput
                  type="switch"
                  checked={archived === true}
                  onClick={(): void => {
                    setState((current) => ({ ...current, filters: { ...current.filters, archived: !archived } }));
                    fetch();
                  }}
                  style={{ marginRight: '1rem' }}
                />
              </div>
            </QueryFilters.Control>
          </HasPermission>
          {['dev', 'stage'].includes(env) && (
            <QueryFilters.Control>
              <FormButton
                name="IMPORT_SCHEDULES"
                icon={<i className="sv sv-download2 fs-4" />}
                variant="outline-light"
                onClick={toggleModal('addonUpload', true)}
              >
                Import Schedules
              </FormButton>
            </QueryFilters.Control>
          )}
          <QueryFilters.Control>
            {archived ? (
              <ConfirmationButton
                icon={<i className="sv sv-undo fs-4" />}
                variant="outline-light"
                disabled={!selected.length}
                onConfirm={unarchiveImports}
                options={{
                  confirmation: {
                    Body: {
                      message: `Unarchiving ${selected?.length} addon(s). Is this ok?`,
                    },
                    Footer: {
                      resolve: 'Yes',
                      reject: 'Cancel',
                    },
                  },
                }}
              >
                {selected?.length > 1 && (
                  <Badge className="ms-2" pill bg="success">
                    {selected?.length}
                  </Badge>
                )}
              </ConfirmationButton>
            ) : (
              <ConfirmationButton
                icon={<i className="sv sv-box fs-4" />}
                variant="outline-light"
                disabled={!selected.length}
                onConfirm={archiveImports}
                options={{
                  confirmation: {
                    Body: {
                      message: `Archiving ${selected?.length} addon(s). Is this ok?`,
                    },
                    Footer: {
                      resolve: 'Yes',
                      reject: 'Cancel',
                    },
                  },
                }}
              >
                {selected?.length > 1 && (
                  <Badge className="ms-2" pill bg="success">
                    {selected?.length}
                  </Badge>
                )}
              </ConfirmationButton>
            )}
          </QueryFilters.Control>
          <QueryFilters.Control>
            <Button variant="outline-light" onClick={toggleModal('addonSettings', true)}>
              <i className="sv sv-cog fs-4" />
            </Button>
          </QueryFilters.Control>
        </div>
      </QueryFilters>
      <AddonSettingsModal show={addonSettings?.show} onHide={toggleModal('addonSettings', false)} />
    </>
  );
};

export default AddonFilters;
